import { motion } from "framer-motion";
import { Zap, Brain, Layers, MessageSquare, BarChart3, Shield } from "lucide-react";
import { ASSETS } from "@/constants/assets";

const SERVICES = [
  { icon: Zap, title: "Intelligent Intake Engine", desc: "Dynamic, AI-guided intake that adapts to patient responses, visit type, and specialty. Outputs structured data ready for clinical use and billing." },
  { icon: Brain, title: "Integrated Matrix Engine", desc: "Proprietary engine mapping patients across multiple domains — metabolic, hormonal, sleep, stress, gut, recovery — generating composite scores and pattern recognition." },
  { icon: Layers, title: "Programs & Protocols", desc: "Central protocol library with condition-based programs, tiered interventions, and intelligent databases for labs, meds, and monitoring schedules." },
  { icon: MessageSquare, title: "AI Assistants & Portal", desc: "Patient portal with scheduling, education, progress dashboards, and AI chatbots that guide intake, check-ins, and FAQs." },
  { icon: BarChart3, title: "Analytics & Operations", desc: "Dashboards for visit volume, intake completion, program enrollment, outcomes trends — designed for MSOs to compare locations." },
  { icon: Shield, title: "EHR/CRM Integration", desc: "Layers onto existing systems via standards and APIs. Not a rip-and-replace — designed to enhance what you already use." },
];

export const ServicesSection = () => (
  <section id="services" className="relative z-[1] py-28 lg:py-36 px-6 lg:px-12 overflow-hidden" data-testid="services-section">
    <div className="logo-watermark-section">
      <img src={ASSETS.logo} alt="" className="logo-glow-intense" />
    </div>
    <div className="max-w-[1400px] mx-auto relative z-[2]">
      <motion.div
        className="bg-gradient-to-r from-[rgba(3,11,24,0.95)] via-[rgba(3,11,24,0.85)] to-transparent p-8 rounded-lg mb-12 max-w-[800px]"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <div className="font-orbitron text-base font-semibold tracking-[5px] uppercase text-[#60A5FA] mb-5 text-shadow">
          The Platform
        </div>
        <h2 className="font-orbitron text-3xl sm:text-4xl lg:text-5xl font-bold tracking-[3px] leading-tight mb-6 text-white text-shadow">
          AI-Powered Clinical<br />Operating System
        </h2>
        <p className="font-outfit text-xl lg:text-2xl font-light leading-relaxed text-white/80 text-shadow-sm">
          Intelligent IT infrastructure that layers onto existing systems — intake, analysis, protocols, chatbots, and patient portals — so clinicians can focus on care.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {SERVICES.map((service, i) => (
          <motion.div
            key={i}
            className="service-card bg-[rgba(10,22,40,0.95)] backdrop-blur-sm border-2 border-[#3B82F6]/40 rounded-lg p-10 transition-all hover:border-[#3B82F6] hover:-translate-y-1 hover:shadow-2xl hover:shadow-[#3B82F6]/20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.1 }}
            data-testid={`service-card-${i}`}
          >
            <div className="w-16 h-16 border-2 border-[#3B82F6]/50 rounded-lg flex items-center justify-center mb-6 bg-[rgba(59,130,246,0.15)]">
              <service.icon className="w-8 h-8 text-[#60A5FA]" />
            </div>
            <h3 className="font-orbitron text-xl lg:text-2xl font-bold tracking-[2px] mb-5 text-white text-shadow">
              {service.title}
            </h3>
            <p className="font-outfit text-lg lg:text-xl leading-relaxed text-white/90 font-light">
              {service.desc}
            </p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);
